import { ApiError, type Api } from "../../../data/api";
import type { BeitragRow, MarkenLink } from "./beitraege-model";
import type { AttachmentLite, BrandRow, CatalogEntry, DealFull, PeriodFull, TalentRow } from "./typen";
import { t } from "../../../lib/sprache";

/**
 * Lader und Schreibwege der Beiträge (0068). Rechte prüft die Datenbank (RLS wie „Kooperation bearbeiten“, Wächter
 * app.guard_deal_deliverable); wo RLS eine Änderung still ins Leere laufen lässt, melden die Schreibwege das ausdrücklich.
 */

export const BEITRAG_SPALTEN =
  "id, org_id, deal_id, period_id, platform, format, format_label, title, draft_due, publish_on, status, live_url, published_on, live_reported_by, " +
  "live_reported_at, label_checked, label_checked_by, label_checked_at, usage_rights_months, usage_rights_until, note, sort_order, created_at, updated_at, approval_required, " +
  "usage_kinds, usage_exclusive, usage_territory";
const ORDER = [["sort_order", "asc"], ["created_at", "asc"]] as const;

function quiet<T>(p: Promise<T[]>): Promise<T[]> {
  return p.catch(() => [] as T[]);
}
function verweigert(was: string): ApiError {
  return new ApiError(t(`${was} nicht möglich: Der Beitrag wurde nicht gefunden oder ist für dein Konto nicht freigegeben.`, `Cannot ${was}: the post was not found or is not available to your account.`), { code: "42501" });
}

/** Beiträge einer Kooperation und die Nachweise der Kennzeichnung (Anhänge). Nicht lesbar ergibt leere Listen. */
export async function ladeBeitraegeDerKooperation(api: Api, dealId: string): Promise<{ beitraege: BeitragRow[]; nachweise: AttachmentLite[] }> {
  const beitraege = await quiet(api.select<BeitragRow>("deal_deliverables", { columns: BEITRAG_SPALTEN, filters: [["deal_id", "eq", dealId]], order: ORDER }));
  const ids = beitraege.map((b) => b.id);
  const nachweise = ids.length
    ? await quiet(api.select<AttachmentLite>("attachments", {
        columns: "id, owner_id, storage_path, file_name, mime_type, size_bytes, created_at",
        filters: [["owner_kind", "eq", "deliverable"], ["owner_id", "in", ids], ["deleted_at", "is", null]],
        order: [["created_at", "asc"]],
      }))
    : [];
  return { beitraege, nachweise };
}

export interface BeitragsUebersicht {
  beitraege: BeitragRow[];
  deals: DealFull[];
  periods: PeriodFull[];
  talents: TalentRow[];
  brands: BrandRow[];
  platforms: CatalogEntry[];
  /** 0073: offene Freigabe-Links (Filter „Bei der Marke“) */
  beiMarke: MarkenLink[];
}

/** Offene Freigabe-Links der Agentur (0073); nicht lesbar ergibt eine leere Liste. */
function ladeOffeneLinks(api: Api, orgId: string): Promise<MarkenLink[]> {
  return quiet(api.select<MarkenLink>("brand_reviews", {
    columns: "deliverable_id, created_at, expires_at, recipient_email, first_opened_at", filters: [["org_id", "eq", orgId], ["status", "eq", "open"]],
  }));
}

/** Alle Beiträge der Agentur, die die angemeldete Person sehen darf, mit Kooperation, Monat, Creator und Marke. */
export async function ladeBeitragsUebersicht(api: Api, orgId: string): Promise<BeitragsUebersicht> {
  const by = [["org_id", "eq", orgId]] as const;
  const [beitraege, deals, periods, talents, brands, platforms, beiMarke] = await Promise.all([
    api.select<BeitragRow>("deal_deliverables", { columns: BEITRAG_SPALTEN, filters: by, order: ORDER }),
    api.select<DealFull>("deals", { filters: by }),
    api.select<PeriodFull>("deal_periods", { filters: by }),
    api.select<TalentRow>("talents", { filters: by, order: [["first_name", "asc"]] }),
    api.select<BrandRow>("brands", { filters: by, order: [["name", "asc"]] }),
    quiet(api.select<CatalogEntry>("org_deal_platforms", { columns: "id, org_id, key, label_de, label_en, active, sort_order", filters: by, order: [["sort_order", "asc"]] })),
    ladeOffeneLinks(api, orgId),
  ]);
  return { beitraege, deals, periods, talents, brands, platforms, beiMarke };
}

/** Kurzüberblick für die Übersicht /agentur: nur Beiträge laufender Kooperationen (Zählung im Modell). */
export async function ladeBeitragsZahlen(api: Api, orgId: string): Promise<Pick<BeitragsUebersicht, "beitraege" | "deals" | "periods" | "beiMarke">> {
  const by = [["org_id", "eq", orgId]] as const;
  const [beitraege, deals, periods, beiMarke] = await Promise.all([
    quiet(api.select<BeitragRow>("deal_deliverables", { columns: BEITRAG_SPALTEN, filters: [...by, ["status", "neq", "cancelled"]] })),
    quiet(api.select<DealFull>("deals", { columns: "id, talent_id, brand_id, title, status, archived_at", filters: by })),
    quiet(api.select<PeriodFull>("deal_periods", { columns: "id, deal_id, month, status, manual_status", filters: by })),
    ladeOffeneLinks(api, orgId),
  ]);
  return { beitraege, deals, periods, beiMarke };
}

// ── Schreibwege ────────────────────────────────────────────────────────────────

/** Einen oder mehrere Beiträge anlegen („mehrere gleiche anlegen“ = mehrere Zeilen). */
export async function legeBeitraegeAn(api: Api, orgId: string, dealId: string, zeilen: readonly Record<string, unknown>[]): Promise<BeitragRow[]> {
  if (!zeilen.length) return [];
  const rows = await api.insert<BeitragRow>("deal_deliverables", zeilen.map((z) => ({ ...z, org_id: orgId, deal_id: dealId })), { columns: BEITRAG_SPALTEN });
  if (rows.length !== zeilen.length) throw verweigert(t("Anlegen", "create"));
  return rows;
}

/**
 * Beiträge aus der kleinen Liste beim Anlegen einer Kooperation (auch mehrere Creator auf einmal): je Monat (Monatsmodelle)
 * oder für den einzigen Monat; sonst ohne Monat. Termine trägt das Team danach in der Kooperation ein.
 */
export async function legeListenBeitraegeAn(api: Api, orgId: string, dealIds: readonly string[], zeilen: readonly Record<string, unknown>[], jeMonat: boolean): Promise<number> {
  let n = 0;
  for (const dealId of dealIds) {
    const periods = await quiet(api.select<{ id: string; month: string }>("deal_periods", { columns: "id, month", filters: [["deal_id", "eq", dealId]], order: [["month", "asc"]] }));
    const ziele: (string | null)[] = periods.length === 1 ? [periods[0]!.id] : jeMonat && periods.length > 1 ? periods.map((p) => p.id) : [null];
    const rows = ziele.flatMap((periodId) => zeilen.map((z) => ({ ...z, period_id: periodId })));
    n += (await legeBeitraegeAn(api, orgId, dealId, rows)).length;
  }
  return n;
}

export async function aendereBeitrag(api: Api, id: string, patch: Record<string, unknown>): Promise<BeitragRow> {
  const rows = await api.update<BeitragRow>("deal_deliverables", [["id", "eq", id]], patch, { columns: BEITRAG_SPALTEN });
  if (!rows[0]) throw verweigert(t("Ändern", "change"));
  return rows[0];
}

/** Stand setzen; „live“ braucht Datum (und am besten den Link), ein anderer Stand nimmt die Veröffentlichung zurück. */
export async function setzeBeitragsStand(api: Api, id: string, stand: string, live?: { datum: string; link: string | null }): Promise<BeitragRow> {
  if (stand === "live") {
    if (!live?.datum) throw new ApiError(t("Für „live“ braucht es das Datum der Veröffentlichung.", "For “live” the publication date is needed."));
    if (live.link?.trim()) {
      await api.rpc("deliverable_report_live", { p_deliverable: id, p_url: live.link.trim(), p_published_on: live.datum });
      return (await api.select<BeitragRow>("deal_deliverables", { columns: BEITRAG_SPALTEN, filters: [["id", "eq", id]] }))[0]!;
    }
    return aendereBeitrag(api, id, { status: "live", published_on: live.datum });
  }
  return aendereBeitrag(api, id, { status: stand });
}

/** Werbekennzeichnung geprüft (Stempel setzt die Datenbank) bzw. Prüfung zurücknehmen. */
export async function setzeKennzeichnung(api: Api, id: string, geprueft: boolean): Promise<BeitragRow> {
  return aendereBeitrag(api, id, { label_checked: geprueft });
}

const NACHWEIS_TYPEN = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

/** Nachweis (Screenshot, PDF) hochladen: Pfadregel <agentur>/deliverable/<beitrag>/<datei> (0013/0068). */
export async function ladeNachweisHoch(api: Api, orgId: string, beitragId: string, file: File): Promise<void> {
  if (!NACHWEIS_TYPEN.includes(file.type)) throw new ApiError(t("Bitte ein Bild (PNG, JPG, WebP) oder ein PDF wählen.", "Please choose an image (PNG, JPG, WebP) or a PDF."));
  if (file.size <= 0 || file.size > 15_728_640) throw new ApiError(t("Die Datei ist leer oder größer als 15 MB.", "The file is empty or larger than 15 MB."));
  const name = file.name.normalize("NFC").replace(/[^\p{L}\p{N}._-]+/gu, "-").replace(/\.{2,}/g, ".").replace(/^[.-]+/, "").slice(-120) || "nachweis";
  const path = `${orgId}/deliverable/${beitragId}/${Date.now()}-${name}`;
  await api.upload("documents", path, file);
  await api.insert("attachments", {
    org_id: orgId, owner_kind: "deliverable", owner_id: beitragId, storage_path: path, file_name: file.name.slice(0, 200) || "Nachweis", mime_type: file.type, size_bytes: file.size,
  }, { columns: "id" });
}

export async function entferneNachweis(api: Api, attachmentId: string): Promise<void> {
  await api.rpc("attachment_delete", { p_id: attachmentId });
}

export async function loescheBeitrag(api: Api, id: string): Promise<void> {
  await api.remove("deal_deliverables", [["id", "eq", id]]);
  const rest = await api.select<{ id: string }>("deal_deliverables", { columns: "id", filters: [["id", "eq", id]] });
  if (rest.length) throw verweigert(t("Löschen", "delete"));
}
